import { buildStatusTypeSummary, mapStatusesForSource, normalizeStatusName } from '@process-advisor/process-miner/processMiner/statusTypeMapper';

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const normalized = String(value).replace('T', ' ').replace(/\//g, '-');
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
};

const secondsBetween = (from, to) => {
  const a = toDate(from);
  const b = toDate(to);
  if (!a || !b) return null;
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / 1000));
};

const resolveActivityLabel = (activity) => {
  return activity?.n ?? activity?.name ?? activity?.activity ?? activity?.label ?? activity?.text ?? activity?.a ?? '';
};

const resolveActivityId = (activity) => {
  return activity?.id ?? activity?.a ?? activity?.activity_id ?? null;
};


const normMetricText = (value) => String(value || '')
  .toLowerCase()
  .trim()
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ');

const isResolvedLikeStatus = (value) => /(resolved|complete|completed|done|fulfilled|restored)/.test(normMetricText(value));
const isTerminalLikeStatus = (value) => /(closed|cancelled|canceled|invalid|rejected|duplicate|withdrawn)/.test(normMetricText(value));

function metricDurationBucketForEvent(event) {
  const fromBucket = event?.from_time_bucket || 'unclassified';
  if (fromBucket && fromBucket !== 'unclassified') return fromBucket;

  // Defensive ServiceNow fallback: if labels were loaded but mapping metadata is
  // stale/missing, still count the common closure-admin dwell correctly.
  if (isResolvedLikeStatus(event?.from_status_name) && isTerminalLikeStatus(event?.to_status_name)) return 'admin';
  if (isResolvedLikeStatus(event?.from_status_name)) return 'admin';
  return 'unclassified';
}

const resolveTaskKey = (task) => task?.id ?? task?.tid ?? task?.task_id ?? task?.k ?? null;

const getDurationSeconds = (row) => {
  const raw = row?.d ?? row?.duration ?? row?.duration_seconds ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

const pickTimestamp = (row) => row?.ts ?? row?.timestamp ?? row?.time ?? null;

const getTaskCreatedTs = (task) => {
  return (
    task?.t1 ??
    task?.created ??
    task?.created_date ??
    task?.createdDate ??
    task?.create_time ??
    task?.t0 ??
    task?.start ??
    null
  );
};

const getTaskCompletedTs = (task) => {
  return task?.t2 ?? task?.closed ?? task?.closed_date ?? task?.end ?? null;
};

function enrichMetricsRows(metricRows = [], taskArray = []) {
  const filteredTasks = (taskArray || []).filter((t) => (t?.filtered ?? 0) === 0);

  const taskEndDate = (task) => {
    const value = task?.t1 ?? task?.closed ?? task?.closed_date ?? task?.end ?? null;
    if (!value) return null;
    return String(value).replace(/\//g, '-').substring(0, 10);
  };

  const avgForTasks = (tasks, key) => {
    if (!tasks.length) return 0;
    const vals = tasks.map((t) => Number(t?.[key] || 0)).filter((v) => Number.isFinite(v));
    if (!vals.length) return 0;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  };

  const sumForTasks = (tasks, key) => tasks.reduce((acc, t) => {
    const n = Number(t?.[key] || 0);
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);

  const aggregateFlowEfficiency = (tasks) => {
    const touch = sumForTasks(tasks, 'touch_time_seconds');
    const wait = sumForTasks(tasks, 'wait_time_seconds');
    const admin = sumForTasks(tasks, 'admin_time_seconds');
    const queue = sumForTasks(tasks, 'queue_time_seconds');
    const denom = touch + wait + admin + queue;
    return denom > 0 ? Math.min(1, touch / denom) : 0;
  };

  const enrichOneRow = (row) => {
    if (!row || typeof row !== 'object') return row;

    const hasWindow = Boolean(row?.start_date && row?.end_date);
    const rowTasks = hasWindow
      ? filteredTasks.filter((task) => {
          const endDate = taskEndDate(task);
          return endDate && endDate >= row.start_date && endDate < row.end_date;
        })
      : filteredTasks;

    const derived = {
      avg_cycle_time_started: avgForTasks(rowTasks, 'cycle_time_started_seconds'),
      avg_resolution_time: avgForTasks(rowTasks, 'resolution_time_seconds'),
      avg_touch_time: avgForTasks(rowTasks, 'touch_time_seconds'),
      avg_wait_time: avgForTasks(rowTasks, 'wait_time_seconds'),
      avg_admin_time: avgForTasks(rowTasks, 'admin_time_seconds'),
      avg_queue_time: avgForTasks(rowTasks, 'queue_time_seconds'),
      avg_unclassified_time: avgForTasks(rowTasks, 'unclassified_time_seconds'),
      avg_flow_efficiency: aggregateFlowEfficiency(rowTasks)
    };

    return {
      ...row,
      // These are derived from the enriched event/status mapping. Do not keep stale
      // zero/default values from modelMetrics that were calculated before enrichment.
      avg_cycle_time_started: derived.avg_cycle_time_started,
      avg_resolution_time: derived.avg_resolution_time,
      avg_touch_time: derived.avg_touch_time,
      avg_wait_time: derived.avg_wait_time,
      avg_admin_time: derived.avg_admin_time,
      avg_queue_time: derived.avg_queue_time,
      avg_unclassified_time: derived.avg_unclassified_time,
      avg_flow_efficiency: derived.avg_flow_efficiency
    };
  };

  if (!metricRows) return metricRows;

  if (Array.isArray(metricRows)) {
    return metricRows.map(enrichOneRow);
  }

  if (typeof metricRows === 'object') {
    return enrichOneRow(metricRows);
  }

  return metricRows;
}

export function enrichProcessArrays({
  activityArray = [],
  stepArray = [],
  eventLogArray = [],
  variantArray = [],
  taskArray = [],
  modelMetrics = [],
  modelMetricsTotal = [],
  sourceSystem = 'jira',
  activityContext = null,
  activityGroupMappings = null
}) {
  const statusNames = activityArray.map(resolveActivityLabel).filter(Boolean);
  const mappings = mapStatusesForSource(statusNames, { sourceSystem, activityContext, activityGroupMappings });
  const mappingByName = new Map(mappings.map((m) => [normalizeStatusName(m.statusName), m]));
  const mappingById = new Map();

  const enrichedActivityArray = activityArray.map((activity) => {
    const statusName = resolveActivityLabel(activity);
    const mapping = mappingByName.get(normalizeStatusName(statusName)) || null;
    const enriched = {
      ...activity,
      status_name: statusName,
      operational_type: mapping?.operationalType || 'unclassified',
      time_bucket: mapping?.timeBucket || 'unclassified',
      mapping_confidence: mapping?.confidence ?? 0,
      mapping_source: mapping?.matchSource || 'fallback',
      mapping_reason: mapping?.reason || 'No mapping',
      ...mapping?.flags
    };
    const id = resolveActivityId(activity);
    if (id !== null && id !== undefined) mappingById.set(id, enriched);
    return enriched;
  });

  const activityTypeById = (id) => mappingById.get(id) || {};

  const enrichedStepArray = stepArray.map((step) => {
    const fromMeta = activityTypeById(step?.f ?? step?.from);
    const toMeta = activityTypeById(step?.t ?? step?.to);
    return {
      ...step,
      from_status_name: fromMeta.status_name || '',
      to_status_name: toMeta.status_name || '',
      from_operational_type: fromMeta.operational_type || 'unclassified',
      to_operational_type: toMeta.operational_type || 'unclassified',
      from_time_bucket: fromMeta.time_bucket || 'unclassified',
      to_time_bucket: toMeta.time_bucket || 'unclassified',
      transition_type: `${fromMeta.time_bucket || 'unclassified'}_to_${toMeta.time_bucket || 'unclassified'}`,
      is_wait_transition: (toMeta.time_bucket || '') === 'wait',
      is_touch_transition: (toMeta.time_bucket || '') === 'touch',
      is_admin_transition: (toMeta.time_bucket || '') === 'admin',
      is_terminal_transition: (toMeta.time_bucket || '') === 'terminal'
    };
  });

  const eventsByTask = new Map();
  const enrichedEventLogArray = eventLogArray.map((event) => {
    const fromMeta = activityTypeById(event?.f ?? event?.from);
    const toMeta = activityTypeById(event?.t ?? event?.to);
    const enriched = {
      ...event,
      from_status_name: fromMeta.status_name || '',
      to_status_name: toMeta.status_name || '',
      from_operational_type: fromMeta.operational_type || 'unclassified',
      to_operational_type: toMeta.operational_type || 'unclassified',
      from_time_bucket: fromMeta.time_bucket || 'unclassified',
      to_time_bucket: toMeta.time_bucket || 'unclassified',
      transition_type: `${fromMeta.time_bucket || 'unclassified'}_to_${toMeta.time_bucket || 'unclassified'}`,
      event_duration_seconds: getDurationSeconds(event),
      timestamp_resolved: pickTimestamp(event)
    };
    const taskId = resolveTaskKey(event);
    if (!eventsByTask.has(taskId)) eventsByTask.set(taskId, []);
    eventsByTask.get(taskId).push(enriched);
    return enriched;
  });

  const enrichedTaskArray = taskArray.map((task) => {
    const directTaskId = resolveTaskKey(task);
    let events = eventsByTask.get(directTaskId) || [];

    if (!events.length && Number.isInteger(task?.si) && Number.isInteger(task?.fi)) {
      events = enrichedEventLogArray.slice(task.si, task.fi + 1);
    }

    events = events.slice().sort((a, b) => String(pickTimestamp(a) || '').localeCompare(String(pickTimestamp(b) || '')));

    let touch = 0;
    let wait = 0;
    let admin = 0;
    let queue = 0;
    let unclassified = 0;
    let firstActiveAt = null;
    let firstWaitAt = null;
    let firstResolvedAt = null;
    let firstClosedAt = null;
    let firstDoneAt = null;

    for (const event of events) {
      const ts = pickTimestamp(event);
      const seconds = getDurationSeconds(event);
      const durationBucket = metricDurationBucketForEvent(event);

      if (event.to_time_bucket === 'touch' && !firstActiveAt) firstActiveAt = ts;
      if (event.to_time_bucket === 'wait' && !firstWaitAt) firstWaitAt = ts;
      if (event.to_operational_type === 'resolved' && !firstResolvedAt) firstResolvedAt = ts;
      if (event.to_operational_type === 'closed' && !firstClosedAt) firstClosedAt = ts;
      if ((event.to_operational_type === 'resolved' || event.to_operational_type === 'closed') && !firstDoneAt) firstDoneAt = ts;

      if (durationBucket === 'touch') touch += seconds;
      else if (durationBucket === 'wait') wait += seconds;
      else if (durationBucket === 'admin') admin += seconds;
      else if (durationBucket === 'queue') queue += seconds;
      else if (durationBucket === 'terminal' || durationBucket === 'no_time') {
        // Terminal/synthetic states do not contribute to flow-time buckets.
      }
      else unclassified += seconds;
    }

    const createdTs = getTaskCreatedTs(task);
    const completedTs = getTaskCompletedTs(task);
    const startTs = firstActiveAt || createdTs || null;
    const endTs = firstDoneAt || firstClosedAt || completedTs || null;
    const resolutionTs = firstResolvedAt || firstDoneAt || firstClosedAt || completedTs || null;

    const cycleStarted = secondsBetween(startTs, endTs);
    const resolutionTime = secondsBetween(createdTs, resolutionTs);
    const totalKnown = touch + wait + admin + queue;
    const flowEfficiency = totalKnown > 0 ? Math.min(1, touch / totalKnown) : 0;

    return {
      ...task,
      first_active_at: firstActiveAt,
      first_wait_at: firstWaitAt,
      first_resolved_at: firstResolvedAt,
      first_closed_at: firstClosedAt,
      first_done_at: firstDoneAt,
      cycle_time_started_seconds: cycleStarted || 0,
      resolution_time_seconds: resolutionTime || 0,
      touch_time_seconds: touch,
      wait_time_seconds: wait,
      admin_time_seconds: admin,
      queue_time_seconds: queue,
      unclassified_time_seconds: unclassified,
      flow_efficiency: flowEfficiency
    };
  });

  const taskByVariant = new Map();
  for (const task of enrichedTaskArray) {
    const variantId = task?.vid ?? task?.v ?? null;
    if (variantId === null || variantId === undefined) continue;
    if (!taskByVariant.has(variantId)) taskByVariant.set(variantId, []);
    taskByVariant.get(variantId).push(task);
  }

  const avgMetric = (tasks, key) => {
    if (!tasks?.length) return 0;
    return tasks.reduce((acc, t) => acc + Number(t?.[key] || 0), 0) / tasks.length;
  };

  const enrichedVariantArray = variantArray.map((variant) => {
    const variantId = variant?.id ?? variant?.v;
    const tasks = taskByVariant.get(variantId) || taskByVariant.get(variant?.v) || [];
    return {
      ...variant,
      avg_cycle_time_started: avgMetric(tasks, 'cycle_time_started_seconds'),
      avg_resolution_time: avgMetric(tasks, 'resolution_time_seconds'),
      avg_touch_time: avgMetric(tasks, 'touch_time_seconds'),
      avg_wait_time: avgMetric(tasks, 'wait_time_seconds'),
      avg_admin_time: avgMetric(tasks, 'admin_time_seconds'),
      avg_queue_time: avgMetric(tasks, 'queue_time_seconds'),
      avg_unclassified_time: avgMetric(tasks, 'unclassified_time_seconds'),
      avg_flow_efficiency: (() => {
        const touch = avgMetric(tasks, 'touch_time_seconds') * (tasks?.length || 0);
        const wait = avgMetric(tasks, 'wait_time_seconds') * (tasks?.length || 0);
        const admin = avgMetric(tasks, 'admin_time_seconds') * (tasks?.length || 0);
        const queue = avgMetric(tasks, 'queue_time_seconds') * (tasks?.length || 0);
        const denom = touch + wait + admin + queue;
        return denom > 0 ? Math.min(1, touch / denom) : 0;
      })()
    };
  });

  const summary = buildStatusTypeSummary(mappings);

  return {
    activityArray: enrichedActivityArray,
    stepArray: enrichedStepArray,
    eventLogArray: enrichedEventLogArray,
    variantArray: enrichedVariantArray,
    taskArray: enrichedTaskArray,
    modelMetrics: enrichMetricsRows(modelMetrics, enrichedTaskArray),
    modelMetricsTotal: enrichMetricsRows(modelMetricsTotal, enrichedTaskArray),
    statusTypeMappings: mappings,
    statusTypeSummary: summary
  };
}